import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { ArrowLeft, User, Package, Heart, ShoppingCart, MapPin, Loader2, Mail, Calendar } from 'lucide-react';

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="bg-secondary/60 border border-border rounded-xl p-5">
    <div className="flex items-center justify-between mb-2">
      <p className="text-xs uppercase tracking-widest text-muted-foreground">{label}</p>
      <Icon className={`h-4 w-4 ${accent || 'text-muted-foreground'}`} />
    </div>
    <p className="text-3xl font-light">{value}</p>
  </div>
);

const AdminCustomerDetail = () => {
  const { id } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (authLoading) return;
    if (!user || !user.is_admin) {
      navigate('/');
      return;
    }
    fetchDetail();
  }, [id, user, authLoading]);

  const fetchDetail = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/customers/${id}`);
      setData(response.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load customer');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="customer-detail-loading">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="py-24 md:py-32 max-w-[1400px] mx-auto px-4 md:px-8 text-center">
        <p className="text-lg text-muted-foreground mb-4">{error || 'Customer not found'}</p>
        <Button onClick={() => navigate('/admin')} variant="outline" className="rounded-full" data-testid="back-to-dashboard-btn">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Dashboard
        </Button>
      </div>
    );
  }

  const { customer, orders, addresses, wishlist, cart, stats } = data;
  const statusColor = (s) => ({
    confirmed: 'bg-green-100 text-green-700',
    pending: 'bg-yellow-100 text-yellow-700',
    shipped: 'bg-blue-100 text-blue-700',
    delivered: 'bg-emerald-100 text-emerald-700',
    failed: 'bg-red-100 text-red-700'
  }[s] || 'bg-gray-100 text-gray-700');

  return (
    <div className="py-24 md:py-32 max-w-[1400px] mx-auto px-4 md:px-8" data-testid="admin-customer-detail-page">
      <Button
        onClick={() => navigate('/admin')}
        variant="ghost"
        className="mb-6 rounded-full"
        data-testid="back-to-dashboard-btn"
      >
        <ArrowLeft className="h-4 w-4 mr-2" /> Back to Dashboard
      </Button>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-10 gap-4">
        <div className="flex items-center gap-4">
          {customer.picture ? (
            <img src={customer.picture} alt={customer.name} className="w-16 h-16 rounded-full border border-border" />
          ) : (
            <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center">
              <User className="h-7 w-7 text-muted-foreground" />
            </div>
          )}
          <div>
            <h1 className="text-3xl md:text-4xl font-light tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }} data-testid="customer-name">
              {customer.name}
            </h1>
            <p className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
              <Mail className="h-3.5 w-3.5" /> {customer.email}
            </p>
            <p className="text-xs text-muted-foreground flex items-center gap-2 mt-1">
              <Calendar className="h-3 w-3" /> Joined {new Date(customer.created_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
        <StatCard icon={Package} label="Total Orders" value={stats.total_orders} accent="text-primary" />
        <StatCard icon={Package} label="Total Spent" value={`₹${(stats.total_spent || 0).toLocaleString('en-IN')}`} accent="text-green-600" />
        <StatCard icon={Heart} label="Wishlist" value={stats.wishlist_count} accent="text-red-500" />
        <StatCard icon={ShoppingCart} label="In Cart" value={stats.cart_count} accent="text-blue-500" />
      </div>

      {/* Orders */}
      <section className="mb-12" data-testid="customer-orders-section">
        <h2 className="text-2xl font-medium mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          Order History
        </h2>
        {orders.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">No orders yet.</p>
        ) : (
          <div className="space-y-4">
            {orders.map((o) => (
              <div key={o.id} className="border border-border p-5 rounded-xl" data-testid={`customer-order-${o.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                  <div>
                    <p className="font-medium">Order #{o.id.slice(0, 8)}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(o.created_at).toLocaleString('en-IN', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                  <span className={`text-xs uppercase tracking-widest px-3 py-1 rounded-full ${statusColor(o.status)}`}>
                    {o.status}
                  </span>
                </div>
                <div className="space-y-1 mb-3 text-sm">
                  {o.items.map((it, idx) => (
                    <div key={idx} className="flex justify-between text-muted-foreground">
                      <span>{it.product_name} × {it.quantity}</span>
                      <span>₹{(it.price * it.quantity).toLocaleString('en-IN')}</span>
                    </div>
                  ))}
                </div>
                {o.special_request && (
                  <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900">
                    <span className="uppercase tracking-widest text-amber-700 mr-2">Special request:</span>
                    {o.special_request}
                  </div>
                )}
                <div className="flex justify-between pt-3 border-t border-border">
                  <span className="text-sm font-medium">Total</span>
                  <span className="text-sm font-light">₹{o.total_amount?.toLocaleString('en-IN')}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
        {/* Wishlist */}
        <section data-testid="customer-wishlist-section">
          <h2 className="text-2xl font-medium mb-6 flex items-center gap-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            <Heart className="h-5 w-5 text-red-500" /> Wishlist ({wishlist.length})
          </h2>
          {wishlist.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">Empty wishlist.</p>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {wishlist.map((p) => (
                <Link to={`/products/${p.id}`} key={p.id} className="border border-border rounded-lg p-3 hover:bg-secondary/40 transition-colors block" data-testid={`customer-wishlist-${p.id}`}>
                  <img src={p.image} alt={p.name} onError={(e) => { e.currentTarget.src = 'https://placehold.co/200x200/f5f5f4/a8a29e?text=No+Image'; }} className="w-full h-24 object-cover rounded mb-2" />
                  <p className="text-xs font-medium line-clamp-1">{p.name}</p>
                  <p className="text-xs text-muted-foreground">₹{p.price?.toLocaleString('en-IN')}</p>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* Cart */}
        <section data-testid="customer-cart-section">
          <h2 className="text-2xl font-medium mb-6 flex items-center gap-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            <ShoppingCart className="h-5 w-5" /> Current Cart ({cart.length})
          </h2>
          {cart.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">Cart is empty.</p>
          ) : (
            <div className="space-y-2">
              {cart.map((ci) => (
                <div key={ci.product_id} className="border border-border rounded-lg p-3 flex items-center gap-3" data-testid={`customer-cart-item-${ci.product_id}`}>
                  <img src={ci.product.image} alt={ci.product.name} onError={(e) => { e.currentTarget.src = 'https://placehold.co/200x200/f5f5f4/a8a29e?text=No+Image'; }} className="w-14 h-14 object-cover rounded flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium line-clamp-1">{ci.product.name}</p>
                    <p className="text-xs text-muted-foreground">Qty {ci.quantity} · ₹{(ci.product.price * ci.quantity).toLocaleString('en-IN')}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Addresses */}
      <section data-testid="customer-addresses-section">
        <h2 className="text-2xl font-medium mb-6 flex items-center gap-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          <MapPin className="h-5 w-5" /> Saved Addresses ({addresses.length})
        </h2>
        {addresses.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">No addresses saved.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {addresses.map((a) => (
              <div key={a.id} className="border border-border rounded-xl p-4" data-testid={`customer-address-${a.id}`}>
                <div className="flex items-center gap-2 mb-1">
                  <p className="font-medium">{a.name}</p>
                  {a.is_default && (
                    <span className="text-[10px] uppercase tracking-widest bg-primary/10 text-primary px-2 py-0.5 rounded-full">Default</span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">{a.phone}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {a.address_line1}{a.address_line2 ? `, ${a.address_line2}` : ''}, {a.city}, {a.state} - {a.pincode}
                </p>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminCustomerDetail;
